import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  CheckCircle2,
  Plane,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Singapore DMC", path: "/singapore-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE Management",
    copy:
      "Full conference production at Marina Bay Sands Expo, Suntec Singapore, Resorts World Sentosa and the Sands Expo & Convention Centre — from 50-pax board meets to 5,000-pax dealer congresses.",
  },
  {
    icon: Briefcase,
    title: "Corporate Group Travel",
    copy:
      "End-to-end programme management for corporate groups — flight blocks, hotel contracting, F&B, transport, and dedicated on-ground account management.",
  },
  {
    icon: Trophy,
    title: "Incentive Programmes",
    copy:
      "Curated incentive experiences — private dinners at Gardens by the Bay, helicopter tours, Sentosa beach buyouts, and behind-the-scenes Singapore experiences.",
  },
  {
    icon: Plane,
    title: "Leisure & FIT",
    copy:
      "Tailored itineraries for couples, families and small groups — heritage walks, hawker tours, family attractions and luxury stays across Marina Bay and Sentosa.",
  },
];

const VENUES = [
  { name: "Marina Bay Sands Expo & Convention Centre", note: "120,000 sqm — Singapore's largest column-free hall" },
  { name: "Suntec Singapore Convention & Exhibition", note: "42,000 sqm — six halls + 31 meeting rooms" },
  { name: "Resorts World Sentosa Convention Centre", note: "Theatre-style halls for 1,500+ delegates" },
  { name: "Sands Expo & Convention Centre", note: "Direct hotel-to-hall access from Marina Bay Sands" },
  { name: "Capella Singapore", note: "Boutique 5-star for incentive buyouts and CEO retreats" },
  { name: "Gardens by the Bay — Flower Field Hall", note: "Iconic gala dinner venue for up to 500 pax" },
];

const FAQS = [
  {
    q: "Why choose Singapore for a MICE programme?",
    a: "Singapore consistently ranks in the top 3 global MICE destinations. It offers world-class infrastructure, Changi Airport connectivity to 380+ cities, a safe and efficient environment, English as a working language, and the deepest concentration of premium meeting venues in Southeast Asia — all within a compact 30-minute radius.",
  },
  {
    q: "What is the lead time for booking a Singapore MICE event?",
    a: "For events of 500+ pax at top-tier venues like Marina Bay Sands or Suntec, we recommend 9–12 months. For mid-sized corporate groups of 100–300 pax, 4–6 months is usually sufficient. For incentive groups of 50–100 pax, we can often turn around proposals within 6–8 weeks of brief.",
  },
  {
    q: "Does Trawelldmc have local Singapore operations?",
    a: "Yes — we operate with on-ground partners and account managers in Singapore who handle every contract, site inspection, and event day. Our central Dubai HQ owns commercial accountability while our Singapore operations team owns ground delivery.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function SingaporeDmc() {
  return (
    <div data-testid="singapore-dmc-page">
      <Seo
        path="/singapore-dmc"
        title="DMC Singapore — Destination Management Company | Trawelldmc"
        description="Trawelldmc is a leading DMC in Singapore. MICE, corporate group travel, and incentive programmes. Local expertise, 20+ years experience. Get a quote."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      {/* HERO */}
      <section className="relative">
        <img
          src={DESTINATION_IMAGES.singapore}
          alt="Singapore group travel and MICE — Trawelldmc"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Singapore DMC · MICE Capital of Asia
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Singapore</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              A premium DMC operating in the world's most efficient MICE city.
              We deliver corporate, incentive and conference programmes across
              Marina Bay, Sentosa and the CBD.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Singapore"
                className="btn-primary"
                data-testid="sg-cta-proposal"
              >
                Get a Singapore proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/quote"
                className="btn-secondary"
                data-testid="sg-cta-ai-quote"
              >
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY SINGAPORE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Singapore</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              The world's most <br />efficient MICE city.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Singapore is a world-class MICE hub. It is consistently rated in
              the global top three for international association meetings and
              corporate events — and for good reason. The country combines a
              hyper-connected airport, a CBD packed with luxury hotels, and
              integrated resort complexes that put a 5,000-pax conference hall
              within minutes of a 2,500-room hotel inventory.
            </p>
            <p>
              For MICE buyers, Singapore solves problems that other
              destinations create. English is the working language. The regulatory
              environment is predictable. AV, F&B, and entertainment standards
              are international-grade. Visa-free access is available to
              passport-holders from over 160 nationalities. And Changi Airport
              connects to 380+ cities globally, making Singapore the easiest
              place in Asia to convene multinational teams.
            </p>
            <p>
              Trawelldmc has worked Singapore for two decades. We've delivered
              everything from 40-pax board offsites at Capella to 2,500-pax
              dealer conferences at Marina Bay Sands. Our team understands
              every venue's quirks — which halls flood at 600 pax, which
              caterers are dependable at scale, which hotels offer the best
              rates for which dates. That experience translates directly into
              tighter budgets, smarter logistics, and zero surprises.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">What we deliver</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Our Singapore DMC services.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="card-flat p-8">
                <s.icon className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                  {s.title}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Where we work in <br />
              <span className="text-[#FF5238]">Singapore.</span>
            </h2>
            <p className="text-white/70 mt-6 leading-relaxed">
              We hold long-standing rate agreements with the city's premier
              MICE venues and hotel groups. Whether you need 500 rooms at one
              property or a multi-hotel block, our contracting team owns the
              negotiation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">
                      {v.name}
                    </div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCENTIVE FOCUS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="overline mb-4">Incentive groups</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Why Singapore is <br />perfect for incentives.
            </h2>
          </div>
          <div className="lg:col-span-6 space-y-5 text-slate-700 leading-relaxed">
            <p>
              For incentive groups, Singapore offers an unusual combination —
              the polish of a global financial capital with the surprise factor
              of a creative destination. Reward your top performers with a
              private dinner inside the Cloud Forest dome at Gardens by the
              Bay. Charter a yacht for sundowners off the southern islands.
              Buy out a rooftop infinity pool for a brand activation. Host an
              awards night under the SuperTrees.
            </p>
            <p>
              Singapore is also exceptionally safe, walkable and clean — three
              factors that matter when you're flying in 200 high-performing
              delegates who don't speak the local language. Trawelldmc curates
              the experiences your competitors can't book.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">FAQ</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {FAQS.map((f, i) => (
              <div key={f.q} data-testid={`sg-faq-${i}`}>
                <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                  {f.q}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Planning a Singapore <br />
              <span className="text-[#FF5238]">programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Singapore proposal. Our team
              responds within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Singapore"
              className="btn-primary w-full justify-center"
              data-testid="sg-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="btn-secondary w-full justify-center"
            >
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE + INTERNAL LINKS */}
      <section className="bg-[#FDFCF8] border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link
              to="/dubai-dmc"
              className="group flex items-start gap-3"
              data-testid="sg-link-dubai"
            >
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  DMC Dubai
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  GCC HQ · MICE & corporate
                </div>
              </div>
            </Link>
            <Link
              to="/malaysia-dmc"
              className="group flex items-start gap-3"
              data-testid="sg-link-malaysia"
            >
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  DMC Malaysia
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  KL · Penang · Langkawi
                </div>
              </div>
            </Link>
            <Link
              to="/thailand-dmc"
              className="group flex items-start gap-3"
              data-testid="sg-link-thailand"
            >
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  DMC Thailand
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Bangkok · Phuket · Chiang Mai
                </div>
              </div>
            </Link>
            <Link
              to="/"
              className="group flex items-start gap-3"
              data-testid="sg-link-home"
            >
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">
                  Trawelldmc Home
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  All destinations & services
                </div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
